import { DynamoDBClient, QueryCommand } from "@aws-sdk/client-dynamodb";
import { unmarshall } from "@aws-sdk/util-dynamodb";
import { useQuery } from "@tanstack/vue-query";
import { ComputedRef, computed } from "vue";

export interface CameraWithSchedule {
	id: string;            // cam_id (for DynamoDB queries)
	scheduleId: string;    // unique schedule_id
	scheduleName: string;
	scheduleStart: number; // Unix seconds
	scheduleEnd: number;   // Unix seconds
}

/**
 * Get all schedules for an event, one entry per schedule (not deduplicated by camera).
 */
export async function getEventCameraIDs(
	projectId: string,
	eventId: string,
	dynamodb: {
		user_table_name: string;
		client: DynamoDBClient;
	},
): Promise<CameraWithSchedule[]> {
	const { client, user_table_name } = dynamodb;
	const cameras: CameraWithSchedule[] = [];

	const pk = `PROJECT#${projectId}#EVENT#${eventId}`;

	const command = new QueryCommand({
		TableName: user_table_name,
		KeyConditionExpression: "PK = :pk AND begins_with(SK, :skPrefix)",
		ProjectionExpression: "cam_id, #name, #schedule_id, #schedule_start, #schedule_end",
		ExpressionAttributeNames: {
			"#name": "name",
			"#schedule_id": "schedule_id",
			"#schedule_start": "schedule_start",
			"#schedule_end": "schedule_end",
		},
		ExpressionAttributeValues: {
			":pk": { S: pk },
			":skPrefix": { S: "CAM_SCHEDULE#" },
		},
	});

	try {
		const result = await client.send(command);
		if (result.Items) {
			for (const item of result.Items) {
				const u = unmarshall(item);
				const camId = u.cam_id as string;
				if (!camId) continue;
				cameras.push({
					id: camId,
					scheduleId: u.schedule_id as string,
					scheduleName: (u.name as string) || camId,
					scheduleStart: u.schedule_start as number,
					scheduleEnd: u.schedule_end as number,
				});
			}
		}
	} catch (err) {
		throw err;
	}

	return cameras.sort((a, b) => a.scheduleStart - b.scheduleStart);
}

export function useEventCameras(
	projectId: ComputedRef<string | null>,
	eventId: ComputedRef<string | null>,
	dynamodb: ComputedRef<{
		user_table_name: string;
		client: DynamoDBClient;
	} | null>,
	credentialsReset: ComputedRef<number>,
) {
	const enabled = computed(
		() => !!projectId.value && !!eventId.value && !!dynamodb.value,
	);

	return useQuery({
		queryKey: ["event-camera-ids", projectId, eventId, credentialsReset],
		queryFn: ({ queryKey: [_, pId, eId] }) =>
			getEventCameraIDs(
				pId as string,
				eId as string,
				dynamodb.value as {
					user_table_name: string;
					client: DynamoDBClient;
				},
			),
		enabled,
	});
}
